import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import cloneDeep from "lodash.clonedeep";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, delay, expand, map, switchMap } from "rxjs/operators";
import { ISignedURL } from "src/app/model/ISignedURL";
import { environment } from "src/environments/environment";
import { ImageSizePrefix } from "../../model/IImage";
import { IInductionPage } from "../../model/IInductionPage";

@Injectable({
	providedIn: "root",
})
export class ImageService {
	readonly hardResetTimerLimit = 60 * 1000;
	constructor(private http: HttpClient) {}

	_resizeImage(s3URL) {
		return this.http.put(`${environment.apiURL}/image-v2/resize`, {
			s3URL,
		});
	}

	ExpectedImageURLForWidth(urlOrig: string, width: number) {
		let url = `${urlOrig}`;
		if (url.indexOf("http://") === 0) {
			url = url.replace("http://", "https://");
		}
		if (url.indexOf("//") === 0) {
			url = url.replace("//", "https://");
		}

		const ext = url.substr(url.lastIndexOf("."));
		if (![".jpg", ".png", ".jpeg"].includes(ext)) {
			return url;
		}
		const replaceExt = new RegExp(ext + "$");
		let expectedExtension;

		if (width < 768) {
			expectedExtension = "-xs" + ext;
		} else if (width < 992) {
			expectedExtension = "-sm" + ext;
		} else if (width < 1300) {
			expectedExtension = "-md" + ext;
		} else {
			expectedExtension = "-lg" + ext;
		}

		if (url.indexOf(expectedExtension) > -1) {
			return url;
		}
		url = url.replace(replaceExt, expectedExtension);

		return url;
	}

	GetCloudFrontURL(url: string) {
		if (
			!url.startsWith("https://") ||
			url.indexOf(environment.cloudFrontContentURL.replace("https://", "")) > 0
		) {
			return url;
		}
		// return url;
		try {
			const cleanUpUrl = url
				.replace(`s3.eu-west-2.amazonaws.com/`, "")
				.replace(`/user-generated-content.inductforwork.co.uk/`, "");
			const key = cleanUpUrl
				.slice(Math.max(0, cleanUpUrl.indexOf("/") + 1))
				.split("?")[0];
			return `${environment.cloudFrontContentURL}/${key}`;
		} catch (error) {
			return url;
		}
	}

	ReplaceImageInHTML(htmlOrig) {
		const images = [];
		let html = `${htmlOrig}`;
		if (!htmlOrig || htmlOrig.length === 0) {
			return htmlOrig;
		}
		const windowWidth = window.innerWidth;
		var div = document.createElement("div");
		div.innerHTML = html;
		const allImages = Array.from(div.getElementsByTagName("img"));
		for (const elm of allImages) {
			const src = elm.getAttribute("src");
			let widthFromProperty = elm.getAttribute("width") ?? elm.style?.width;
			const match = widthFromProperty.match(/[0-9]+/g);
			if (match?.length > 0) {
				widthFromProperty = match[0];
			}
			const width = Number(widthFromProperty ?? `${windowWidth}` ?? "");
			if (src && src.length) {
				const replaceWith = this.ExpectedImageURLForWidth(src, width);
				const cloudFrontURL = this.GetCloudFrontURL(replaceWith);
				if (src !== cloudFrontURL) {
					elm.setAttribute("src", cloudFrontURL);
					elm.className += " img-fluid";
					images.push(src);
				}
			}
		}

		return div.innerHTML;
	}

	ReplaceImageInPages(pages: IInductionPage[]) {
		return pages.map((page) => {
			const pageCopy = cloneDeep(page) as IInductionPage;
			pageCopy.Information = this.ReplaceImageInHTML(pageCopy.Information);
			pageCopy.PageQuestion = this.ReplaceImageInHTML(pageCopy.PageQuestion);
			return pageCopy;
		});
	}

	RotateImage(url, action, size: ImageSizePrefix = "sm") {
		let timeCountFlag = 0;
		return this.http
			.put(`${environment.apiURL}/image-v2/${action}`, {
				s3URL: url,
			})
			.pipe(
				delay(10 * 1000),
				switchMap((result: { key: string; link: string }) => {
					const checkStatus = () => {
						timeCountFlag += 5 * 1000;

						return this.http
							.put(`${environment.apiURL}/image-v2/exists`, {
								s3URL: this.AddSizePrefix(result.link, size),
							})
							.pipe(
								map((i) => of(i)),
								catchError(() => of(false))
							);
					};
					return checkStatus().pipe(
						expand((res: boolean) => {
							if (res || timeCountFlag >= this.hardResetTimerLimit) {
								return EMPTY;
							}
							return checkStatus().pipe(delay(5 * 1000));
						}),
						switchMap(() => {
							return of(result);
						})
					);
				})
			);
	}

	ResizeImage(
		result: {
			fileName: string;
			fileType: string;
			link: string;
			publicLink: string;
			key: string;
		},
		size: ImageSizePrefix = "sm"
	) {
		let timeCountFlag = 0;
		return this._resizeImage(result.link).pipe(
			delay(10 * 1000),
			switchMap(() => {
				const checkStatus = () => {
					timeCountFlag += 5 * 1000;

					return this.http
						.put(`${environment.apiURL}/image-v2/exists`, {
							s3URL: this.AddSizePrefix(result.link, size),
						})
						.pipe(
							map((i) => of(i)),
							catchError(() => of(false))
						);
				};
				return checkStatus().pipe(
					expand((res: boolean) => {
						if (res || timeCountFlag >= this.hardResetTimerLimit) {
							return EMPTY;
						}
						return checkStatus().pipe(delay(5 * 1000));
					})
				);
			}),
			switchMap(() => {
				return of(result);
			}),
			catchError((err) => {
				console.log(err);
				return of(result);
			})
		);
	}

	AddSizePrefix(url: string, size: ImageSizePrefix): string {
		const ext = url.substr(url.lastIndexOf("."));
		return url.replace(ext, `-${size}${ext}`);
	}

	GetSignedURL(
		readOnlySignedURL: string,
		src: string,
		clientWidth?: number
	): Observable<ISignedURL> {
		return this.http.post<ISignedURL>(`${environment.apiURL}${readOnlySignedURL}`, {
			src,
			clientWidth,
		});
	}

}
